import React from "react";

import {
  getBreakdown,
  getUsageSeries,
  ME_INSIGHTS_BASE,
  orgInsightsBase,
} from "../../lib/api";
import type { AuthHeaders } from "../../lib/auth";
import type {
  DateRange,
  Me,
  UsageKeyFacet,
  UsageSeries,
} from "../../lib/types";
import {
  PeriodStepper,
  Card,
  EmptyState,
  Grid,
  Segmented,
  Select,
  Skeleton,
  StatTile,
  type Option,
} from "../../design";
import { TimeChart } from "./shared";
import { humanize, pct, shortDay, usdShort } from "./util";

type Metric = "tokens_saved" | "pre_tokens" | "requests";

const METRICS: { value: Metric; label: string }[] = [
  { value: "tokens_saved", label: "Tokens saved" },
  { value: "pre_tokens", label: "Tokens processed" },
  { value: "requests", label: "Requests" },
];

export const MONEY_SAVED_TIP =
  "What your requests would have cost at API prices without condense, minus what they cost with it. Only the input side counts.";

const NO_TRAFFIC_NEXT = "Shows up after your first session through condense.";

/** The key filter's options, or null when fewer than two keys carried
 * traffic: with one key the filter could only repeat the totals. */
export function keyOptions(
  facets: UsageKeyFacet[] | undefined,
): Option[] | null {
  const used = (facets ?? []).filter((f) => f.requests > 0);
  if (used.length < 2) return null;
  return [
    { value: "all", label: "All API keys" },
    ...used.map((f) => ({
      value: f.key_id ?? "none",
      label: f.revoked ? `${f.label} (revoked)` : f.label,
    })),
  ];
}

function monthKey(y: number, m: number): string {
  return `${y}-${String(m + 1).padStart(2, "0")}`;
}

/** What condense itself measured: tokens it took off the wire, by month. */
export function Savings({
  authHeaders,
  orgId,
  me,
  range,
}: {
  authHeaders: AuthHeaders;
  orgId?: string;
  me?: Me;
  /** The page's date range; without one the tab steps by calendar month. */
  range?: DateRange;
}) {
  const now = new Date();
  const [cursor, setCursor] = React.useState({
    y: now.getFullYear(),
    m: now.getMonth(),
  });
  const [apiKey, setApiKey] = React.useState("all");
  const [metric, setMetric] = React.useState<Metric>("tokens_saved");
  const [mode, setMode] = React.useState<"daily" | "cumulative">("daily");
  const [series, setSeries] = React.useState<UsageSeries | null>(null);
  // Facets are counted before the key filter, so the last ones stay valid
  // while a newly picked key loads.
  const [facets, setFacets] = React.useState<UsageKeyFacet[] | undefined>();
  // With a page range, Money saved comes from the same analytics totals as
  // Summary and Spend, so the three tabs show one figure.
  const [savedUsd, setSavedUsd] = React.useState<string | null>(null);
  React.useEffect(() => {
    if (!range) return;
    let live = true;
    setSavedUsd(null);
    getBreakdown(
      authHeaders,
      orgId ? orgInsightsBase(orgId) : ME_INSIGHTS_BASE,
      range,
      "none",
    ).then(
      (b) => live && setSavedUsd(b.totals.saved_usd),
      () => {},
    );
    return () => {
      live = false;
    };
  }, [authHeaders, orgId, range?.start, range?.end]);
  const [error, setError] = React.useState<string | null>(null);
  const month = monthKey(cursor.y, cursor.m);
  const isNow = cursor.y === now.getFullYear() && cursor.m === now.getMonth();
  const monthLabel = new Date(cursor.y, cursor.m, 1).toLocaleDateString(
    "en-US",
    { month: "long", year: "numeric" },
  );
  const spanLabel = range
    ? `${shortDay(range.start)} – ${shortDay(range.end)}`
    : `calendar ${monthLabel}`;
  const noTraffic = !!me && !me.usage?.request_count && !me.usage?.tokens_saved;

  React.useEffect(() => {
    let live = true;
    setSeries(null);
    setError(null);
    getUsageSeries(
      authHeaders,
      range ?? month,
      "intercept",
      orgId,
      apiKey === "all" ? "" : apiKey,
    ).then(
      (s) => {
        if (!live) return;
        setSeries(s);
        setFacets(s.keys);
      },
      (e: Error) => live && setError(e.message),
    );
    return () => {
      live = false;
    };
  }, [authHeaders, month, orgId, apiKey, range?.start, range?.end]);

  const shift = (n: number) =>
    setCursor((c) => {
      const d = new Date(c.y, c.m + n, 1);
      return { y: d.getFullYear(), m: d.getMonth() };
    });
  const keys = orgId ? null : keyOptions(facets);
  const keyGone = apiKey !== "all" && !keys?.some((k) => k.value === apiKey);
  React.useEffect(() => {
    if (keyGone) setApiKey("all");
  }, [keyGone]);

  const t = series?.totals;
  const latency =
    t && t.requests > 0 && Number.isFinite(t.avg_added_ms)
      ? t.avg_added_ms
      : null;
  let run = 0;
  const days = (series?.buckets ?? []).map((b) => {
    const v = b.slices.reduce((s, x) => s + Number(x[metric] ?? 0), 0);
    run += v;
    return { day: b.date, values: { v: mode === "daily" ? v : run } };
  });

  return (
    <div className="ana-stack">
      {(!range || keys) && (
        <div className="ana-chips">
          {!range && (
            <PeriodStepper
              unit="month"
              label={monthLabel}
              current={isNow}
              onShift={shift}
            />
          )}
          {keys && (
            <Select
              label="API key"
              value={apiKey}
              options={keys}
              onChange={setApiKey}
            />
          )}
        </div>
      )}
      {error ? (
        <EmptyState icon="alert" title="Couldn't load savings">
          {error}
        </EmptyState>
      ) : !series || !t ? (
        <>
          <Skeleton height={88} />
          <Skeleton height={260} />
        </>
      ) : (
        <>
          <Grid cols={latency == null ? 4 : 5}>
            <StatTile
              label="Money saved"
              value={usdShort(savedUsd ?? t.money_saved)}
              info={MONEY_SAVED_TIP}
              note={`API prices, ${spanLabel}`}
            />
            <StatTile
              label="Tokens saved"
              info="Tokens condense removed before they reached your provider, out of everything your agents sent in."
              value={humanize(t.tokens_saved)}
              note={`of ${humanize(t.pre_tokens)} sent in`}
            />
            <StatTile
              label="Compression"
              info="Tokens saved as a share of tokens sent in. What is left is forwarded to your provider."
              value={pct(t.pre_tokens ? t.tokens_saved / t.pre_tokens : null)}
              note={`${humanize(t.post_tokens)} forwarded`}
            />
            <StatTile label="Requests" value={humanize(t.requests)} />
            {latency != null && (
              <StatTile
                label="Added latency"
                info="The time condense adds to a request before passing it on to your provider."
                value={`${Math.round(latency)} ms`}
                note="Average per request"
              />
            )}
          </Grid>
          <Card
            title={`${METRICS.find((m) => m.value === metric)?.label} per day`}
            info="Measured by condense, not taken from your provider's bill."
            actions={
              <>
                <Select
                  label="Show"
                  size="sm"
                  value={metric}
                  options={METRICS}
                  onChange={setMetric}
                />
                <Segmented
                  label="Accumulation"
                  toggle
                  size="sm"
                  value={mode}
                  options={[
                    { value: "daily", label: "Daily" },
                    { value: "cumulative", label: "Cumulative" },
                  ]}
                  onChange={setMode}
                />
              </>
            }
          >
            {days.every((d) => d.values.v === 0) ? (
              <EmptyState compact title="Nothing measured this month">
                {noTraffic ? NO_TRAFFIC_NEXT : undefined}
              </EmptyState>
            ) : (
              <TimeChart
                days={days}
                order={["v"]}
                colorOf={() => "var(--ds-saved)"}
                labelOf={() =>
                  METRICS.find((m) => m.value === metric)?.label ?? ""
                }
                format={(v) => humanize(Math.round(v))}
                kind={mode === "daily" ? "bars" : "area"}
              />
            )}
          </Card>
        </>
      )}
    </div>
  );
}
